/**
 * Foreground-reload effect body — extracted from useChat.ts (issue #3280).
 *
 * Holds the body of the "when the app returns to the foreground after being
 * backgrounded, reload messages from the server" effect. The orchestrator keeps
 * the `useEffect` shell AND its byte-identical `[token, clearActivity]`
 * dependency array; only the effect body lives here. It sets up the
 * `visibilitychange` listener and returns the matching cleanup, so the caller
 * can `return reloadMessagesOnForeground({...})` from inside the effect.
 *
 * Web-internal module: must NOT be promoted to a shared `@xavior/*` package.
 */
import type React from 'react'
import { getConversationChatMessages } from '@/lib/api/endpoints/chat'
import { serverToClientMessage } from '../utils/message-mapping'
import type { ChatMessage } from './useChat.types'

export interface ForegroundReloadDeps {
  token: string | null
  abortControllerRef: React.MutableRefObject<AbortController | null>
  currentSessionIdRef: React.MutableRefObject<string | undefined>
  lastStreamEventAtRef: React.MutableRefObject<number>
  elapsedTimerRef: React.MutableRefObject<ReturnType<typeof setInterval> | null>
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>
  setThinking: React.Dispatch<React.SetStateAction<string | null>>
  setIsStreaming: React.Dispatch<React.SetStateAction<boolean>>
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  setStreamingMessage: React.Dispatch<React.SetStateAction<ChatMessage | null>>
  clearActivity: () => void
}

/**
 * Sets up the visibilitychange listener and returns its cleanup. Extracted
 * verbatim from useChat.ts; the caller does `return reloadMessagesOnForeground(...)`
 * inside `useEffect(..., [token, clearActivity])`.
 */
export function reloadMessagesOnForeground(deps: ForegroundReloadDeps): () => void {
  const {
    token,
    abortControllerRef,
    currentSessionIdRef,
    lastStreamEventAtRef,
    elapsedTimerRef,
    setMessages,
    setThinking,
    setIsStreaming,
    setIsLoading,
    setStreamingMessage,
    clearActivity,
  } = deps

  // Track when the tab was hidden so we know how long we were away
  let hiddenAtMs = 0
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      hiddenAtMs = Date.now()
      return
    }
    if (document.visibilityState !== 'visible') return

    // Only act if a request was in flight when we left
    if (!abortControllerRef.current) return

    const sessionIdSnapshot = currentSessionIdRef.current
    const tokenSnapshot = token
    if (!sessionIdSnapshot || !tokenSnapshot) return

    // If the stream received an event recently, it's still alive — don't abort.
    // Reports and web searches can have 60-90s gaps between SSE events while
    // the LLM is thinking/searching, so use a generous window.
    const msSinceLastEvent = Date.now() - lastStreamEventAtRef.current
    const msHidden = hiddenAtMs > 0 ? Date.now() - hiddenAtMs : Infinity
    if (msSinceLastEvent < 120_000 && msHidden < 180_000) {
      console.log('[useChat] Stream still alive after tab switch, not aborting', {
        msSinceLastEvent,
        msHidden,
      })
      return
    }

    console.log('[useChat] Aborting stale stream after tab return', {
      msSinceLastEvent,
      msHidden,
    })

    // Abort the dead stream and reset all streaming state
    abortControllerRef.current.abort()
    abortControllerRef.current = null
    setIsStreaming(false)
    setIsLoading(true)
    setStreamingMessage(null)
    setThinking(null)
    clearActivity()
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current)
      elapsedTimerRef.current = null
    }

    // Reload messages — server may have finished processing while we were away
    getConversationChatMessages(tokenSnapshot, sessionIdSnapshot, { limit: 100 })
      .then(({ messages: serverMessages }) => {
        if (currentSessionIdRef.current !== sessionIdSnapshot) return
        const clientMessages = serverMessages
          .filter(msg => msg.role === 'user' || msg.role === 'assistant')
          .map(serverToClientMessage)
        setMessages(clientMessages)
      })
      .catch((err) => {
        console.error('[useChat] Failed to reload messages after returning to foreground:', err)
      })
      .finally(() => {
        if (currentSessionIdRef.current !== sessionIdSnapshot) return
        setIsLoading(false)
      })
  }

  document.addEventListener('visibilitychange', handleVisibilityChange)
  return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
}
